export interface UserWorkScheduler {
    dayOfWeek: number;
    startTime: string | null;
    endTime: string | null;
    [key: string]: any;
}

export const USER_WEEKDAYS = [
    {label: 'Понеділок', dayOfWeek: 1},
    {label: 'Вівторок', dayOfWeek: 2},
    {label: 'Середа', dayOfWeek: 3},
    {label: 'Четвер', dayOfWeek: 4},
    {label: "П'ятниця", dayOfWeek: 5},
    {label: 'Субота', dayOfWeek: 6},
    {label: 'Неділя', dayOfWeek: 0},
];

const DEFAULT_USER_WORK_SCHEDULE_BY_DAY: Record<number, {startTime: string; endTime: string}> = {
    0: {startTime: '09:00:00', endTime: '18:00:00'},
    1: {startTime: '09:00:00', endTime: '18:00:00'},
    2: {startTime: '09:00:00', endTime: '18:00:00'},
    3: {startTime: '09:00:00', endTime: '18:00:00'},
    4: {startTime: '09:00:00', endTime: '18:00:00'},
    5: {startTime: '09:00:00', endTime: '18:00:00'},
    6: {startTime: '09:00:00', endTime: '18:00:00'},
};

export const EMPTY_WORK_SCHEDULE_MESSAGE = 'Графік роботи не обрано! Розклад не може бути пустий!*';
export const INVALID_WORK_SCHEDULE_TIME_MESSAGE = 'Відсутній час або час початку перевищує час завершення*';

export function createDefaultUserWorkSchedulers(): UserWorkScheduler[] {
    return [1, 2, 3, 4, 5].map((dayOfWeek) => ({
        dayOfWeek,
        ...DEFAULT_USER_WORK_SCHEDULE_BY_DAY[dayOfWeek],
    }));
}

export function defaultUserWorkScheduler(dayOfWeek: number): UserWorkScheduler {
    const defaults = DEFAULT_USER_WORK_SCHEDULE_BY_DAY[dayOfWeek] || DEFAULT_USER_WORK_SCHEDULE_BY_DAY[1];
    return {
        dayOfWeek,
        startTime: defaults.startTime,
        endTime: defaults.endTime,
    };
}

export function normalizeSchedulerTime(value: unknown): string | null {
    if (value == null || value === '') {
        return null;
    }
    const match = String(value).match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!match) {
        return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3] ?? 0);
    if (hours > 23 || minutes > 59 || seconds > 59) {
        return null;
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function schedulerTimeToDate(value: unknown): Date | null {
    const normalized = normalizeSchedulerTime(value);
    if (!normalized) {
        return null;
    }
    const [hours, minutes, seconds] = normalized.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, seconds, 0);
    return date;
}

export function dateToSchedulerTime(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
}

export function normalizeUserWorkSchedulers(workSchedulers: any): UserWorkScheduler[] {
    if (!Array.isArray(workSchedulers)) {
        return [];
    }
    return workSchedulers.map((day) => ({
        ...day,
        dayOfWeek: Number(day?.dayOfWeek),
        startTime: normalizeSchedulerTime(day?.startTime),
        endTime: normalizeSchedulerTime(day?.endTime),
    }));
}

export function getUserWorkScheduleError(workSchedulers: any): {workSchedulers: string} | null {
    const days = normalizeUserWorkSchedulers(workSchedulers);
    if (!days.length) {
        return {workSchedulers: EMPTY_WORK_SCHEDULE_MESSAGE};
    }

    const valid = days.every((day) => {
        if (!Number.isInteger(day.dayOfWeek) || day.dayOfWeek < 0 || day.dayOfWeek > 6) {
            return false;
        }
        const start = schedulerTimeToDate(day.startTime);
        const end = schedulerTimeToDate(day.endTime);
        if (!start || !end) {
            return false;
        }
        return start.getTime() < end.getTime();
    });

    if (!valid) {
        return {workSchedulers: INVALID_WORK_SCHEDULE_TIME_MESSAGE};
    }

    return null;
}
